/**
 * Biblioteca — v1.16 · point #2.
 *
 * Galería personal de componentes reutilizables. Cada asset muestra:
 *   miniatura · nombre · categoría · fecha · reutilizar · copiar · favorito
 *
 * Backend: /api/library/components (owner_only=true garantiza aislamiento).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Copy, RefreshCcw, Star, Loader2 } from 'lucide-react';
import { apiService } from '../services/api';
import { toast } from 'sonner';

type Asset = {
  id: string;
  owner_id?: string;
  name: string;
  category: string;
  description?: string;
  tags?: string[];
  thumbnail_url?: string | null;
  favorite?: boolean;
  created_at?: string;
  updated_at?: string;
  reuse_count?: number;
  current_version?: string;
};

const CATEGORIES = [
  { id: 'all',        label: 'Todo' },
  { id: 'landing',    label: 'Landing Pages' },
  { id: 'dashboard',  label: 'Dashboards' },
  { id: 'component',  label: 'Componentes' },
  { id: 'background', label: 'Fondos' },
  { id: 'form',       label: 'Formularios' },
  { id: 'card',       label: 'Tarjetas' },
  { id: 'button',     label: 'Botones' },
  { id: 'animation',  label: 'Animaciones' },
  { id: 'hero',       label: 'Secciones Hero' },
  { id: 'layout',     label: 'Layouts' },
];

export default function Biblioteca() {
  const nav = useNavigate();
  const [items, setItems] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { owner_only: true };
      if (category !== 'all') params.category = category;
      if (q.trim()) params.q = q.trim();
      const r = await (apiService as any).api.get('/library/components', { params });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (err: any) {
      toast.error('No pudimos cargar tu biblioteca.');
    } finally {
      setLoading(false);
    }
  }, [category, q]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items, [items]);

  const toggleFav = async (a: Asset) => {
    setBusyId(a.id);
    try {
      await (apiService as any).api.patch(`/library/components/${a.id}`, { favorite: !a.favorite });
      setItems(prev => prev.map(x => x.id === a.id ? { ...x, favorite: !a.favorite } : x));
    } catch { toast.error('No se pudo actualizar.'); }
    finally { setBusyId(null); }
  };

  const copySnippet = async (a: Asset) => {
    try {
      const r = await (apiService as any).api.get(`/library/components/${a.id}`);
      const files = (r.data?.versions?.[0]?.files) || [];
      const snippet = files.map((f: any) => `// ${f.path}\n${f.content}`).join('\n\n');
      await navigator.clipboard.writeText(snippet || a.description || a.name);
      toast.success('Copiado al portapapeles.');
    } catch { toast.error('No se pudo copiar.'); }
  };

  const reuse = async (a: Asset) => {
    setBusyId(a.id);
    try {
      // Send the user to Home with a seed prompt referencing this asset.
      const seed = `Reutiliza el componente "${a.name}" de mi biblioteca como base y adáptalo al nuevo proyecto.`;
      sessionStorage.setItem('nexus_composer_seed', seed);
      nav('/home');
    } finally { setBusyId(null); }
  };

  return (
    <div className="min-h-screen text-slate-100" style={{ background: 'hsl(var(--bg))' }} data-testid="biblioteca-page">
      <header className="sticky top-0 z-20 backdrop-blur-md"
        style={{ background: 'rgba(3,4,7,0.72)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => nav('/home')} className="w-9 h-9 rounded-lg grid place-items-center"
            style={{ background: 'hsl(var(--bg-elev))', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="biblioteca-back" aria-label="Volver">
            <ArrowLeft size={16} />
          </button>
          <div className="flex-1 text-center">
            <div className="text-xs tracking-[0.28em] font-semibold uppercase">Biblioteca</div>
          </div>
          <div className="w-9" />
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-5 space-y-4">
        <div>
          <h1 className="text-2xl font-semibold" style={{ color: '#FFFFFF' }}>Tus componentes reutilizables</h1>
          <p className="text-sm mt-1" style={{ color: '#9BA1A6' }}>
            Reutiliza landing pages, hero sections, formularios y más entre proyectos.
          </p>
        </div>

        {/* Search + chips */}
        <div className="nx-glass p-3 flex items-center gap-2">
          <Search size={14} style={{ color: '#9BA1A6' }} />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por nombre, etiqueta o descripción…"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-500"
            data-testid="biblioteca-search"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" data-testid="biblioteca-chips">
          {CATEGORIES.map(c => (
            <button
              key={c.id}
              onClick={() => setCategory(c.id)}
              data-testid={`biblioteca-cat-${c.id}`}
              className="whitespace-nowrap text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-full transition"
              style={{
                background: category === c.id ? 'hsl(var(--accent) / 0.14)' : 'transparent',
                border: `1px solid ${category === c.id ? 'hsl(var(--accent) / 0.5)' : 'rgba(255,255,255,0.08)'}`,
                color: category === c.id ? 'hsl(var(--accent))' : '#9BA1A6',
              }}
            >{c.label}</button>
          ))}
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16" style={{ color: '#9BA1A6' }}>
            <Loader2 size={20} className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="nx-glass p-10 text-center" data-testid="biblioteca-empty">
            <div className="text-sm font-medium">Aún no has guardado nada en tu biblioteca</div>
            <div className="text-xs mt-1" style={{ color: '#9BA1A6' }}>
              Cuando termines un proyecto, cualquier sección puede guardarse aquí para reutilizarla en tus próximos proyectos.
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map(a => (
              <AssetCard
                key={a.id} asset={a}
                busy={busyId === a.id}
                onToggleFav={() => toggleFav(a)}
                onCopy={() => copySnippet(a)}
                onReuse={() => reuse(a)}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

const AssetCard: React.FC<{
  asset: Asset;
  busy: boolean;
  onToggleFav: () => void;
  onCopy: () => void;
  onReuse: () => void;
}> = ({ asset, busy, onToggleFav, onCopy, onReuse }) => {
  const cat = CATEGORIES.find(c => c.id === asset.category)?.label || asset.category;
  const date = asset.updated_at ? new Date(asset.updated_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' }) : '';
  const initial = (asset.name || 'N').charAt(0).toUpperCase();
  return (
    <div className="nx-glass overflow-hidden group" data-testid={`biblioteca-asset-${asset.id}`}>
      <div className="relative aspect-[16/10]"
        style={{
          background: 'linear-gradient(135deg, hsl(var(--accent) / 0.14), hsl(var(--bg-elev) / 0.4))',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
        {asset.thumbnail_url ? (
          <img src={asset.thumbnail_url} alt="" className="w-full h-full object-cover"
               onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }} />
        ) : (
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-5xl font-light font-mono" style={{ color: 'hsl(var(--accent))', textShadow: '0 0 20px hsl(var(--accent) / 0.6)' }}>
              {initial}
            </div>
          </div>
        )}
        <button
          onClick={onToggleFav}
          disabled={busy}
          className="absolute top-2 right-2 w-8 h-8 rounded-full grid place-items-center backdrop-blur"
          style={{
            background: 'rgba(0,0,0,0.5)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: asset.favorite ? '#FFD54A' : '#FFFFFF',
          }}
          data-testid={`biblioteca-fav-${asset.id}`}
          aria-label="Favorito"
        >
          <Star size={14} fill={asset.favorite ? '#FFD54A' : 'transparent'} />
        </button>
      </div>
      <div className="p-3">
        <div className="flex items-baseline justify-between gap-2">
          <div className="text-sm font-medium truncate" style={{ color: '#FFFFFF' }}>{asset.name}</div>
          <div className="text-[10px] shrink-0" style={{ color: '#687076' }}>{date}</div>
        </div>
        <div className="text-[10px] uppercase tracking-widest mt-0.5" style={{ color: 'hsl(var(--accent))' }}>{cat}</div>
        <div className="mt-3 flex items-center gap-2">
          <button onClick={onReuse} disabled={busy}
            className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'hsl(var(--accent))', color: 'hsl(210 40% 8%)' }}
            data-testid={`biblioteca-reuse-${asset.id}`}
          >
            <RefreshCcw size={11} /> Reutilizar
          </button>
          <button onClick={onCopy} disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs"
            style={{ background: 'hsl(var(--bg-elev) / 0.6)', border: '1px solid rgba(255,255,255,0.08)', color: '#FFFFFF' }}
            data-testid={`biblioteca-copy-${asset.id}`}
          >
            <Copy size={11} /> Copiar
          </button>
        </div>
      </div>
    </div>
  );
};
